import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  TooltipProps,
} from 'recharts';
import { CategoricalChartFunc } from 'recharts/types/chart/generateCategoricalChart';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import {
  getChartDomain,
  getChartInterval,
  getChartOffset,
  hexColorWithOpacity,
  formatLargeAmount,
} from '@libs/helpers';
import { formatDate, useDimensions } from '@libs/hooks';
import { screenSizes } from '@libs/theme';
import { CurrencySymbol } from '@libs/types';

import { YAxisOrientations } from 'src/constants';

import { ChartTooltip } from '../chart-tooltip/ChartTooltip';
import { TooltipRects } from '../chart-tooltip/styles';

export type ChartData = {
  createdAt?: Date | string;
  amount?: number | string;
};

type ActiveDotProps = {
  cx: number;
  cy: number;
};

type InteractiveChartProps<T> = {
  data?: T[];
  currencySymbol?: CurrencySymbol;
  fontSize?: number;
  fontHeight?: number;
  dateFormat?: string | ((val: string) => string);
  shouldShowGraph?: boolean;
  shouldHideXAxis?: boolean;
  shouldHideYAxis?: boolean;
  shouldHideTooltip?: boolean;
  tooltipPrecision?: number;
  customMargin?: {
    top: number;
    left: number;
    right: number;
    bottom: number;
  };
  assetCode?: string;
  chartColor?: string;
  shouldHaveFadeEffect?: boolean;
  tooltipTitle?: string;
  tooltipIcon?: JSX.Element | string;
  yAxisOrientation?: YAxisOrientations;
  isBaseValueMin?: boolean;
  tooltipTimeFormat?: string;
  yAxisTickCount?: number;
  showAmountPrefix?: boolean;
};

const InteractiveChart = <T extends ChartData>({
  data,
  currencySymbol,
  dateFormat,
  fontSize,
  fontHeight,
  customMargin,
  shouldHideXAxis,
  shouldShowGraph = true,
  tooltipPrecision,
  shouldHideYAxis,
  shouldHideTooltip = false,
  assetCode,
  chartColor,
  shouldHaveFadeEffect,
  tooltipIcon,
  tooltipTitle,
  yAxisOrientation = YAxisOrientations.RIGHT,
  isBaseValueMin,
  tooltipTimeFormat,
  yAxisTickCount,
  showAmountPrefix,
}: InteractiveChartProps<T>) => {
  const { colors } = useTheme();
  const { width } = useDimensions();

  const [areaDotColor, setAreaDotColor] = useState('transparent');
  const [chartRects, setChartRects] = useState<TooltipRects>({
    surfaceRect: undefined,
    gridRect: undefined,
  });

  const chartData = useMemo(() => {
    if (!data || !data.length) return { min: 0, max: 0, defaultAmount: 0 };

    const defaultAmount = chartColor ? undefined : Number(data[0]?.amount || 0);

    const amounts = data.map((e) => Number(e.amount));
    return {
      min: Math.min(...amounts),
      max: Math.max(...amounts),
      defaultAmount,
    };
  }, [data, chartColor]);

  const { min: chartMin, max: chartMax, defaultAmount } = chartData;

  const domain = useMemo(
    () => getChartDomain(chartMin, chartMax),
    [chartMin, chartMax]
  );

  const off = useMemo(() => {
    if (!defaultAmount) {
      return 0;
    }

    return getChartOffset(defaultAmount, chartMin, chartMax);
  }, [defaultAmount, chartMin, chartMax]);

  const gradientDefinitions = useMemo(() => {
    if (chartColor) {
      return (
        <defs>
          <linearGradient
            id={`stroke${assetCode || ''}`}
            x1="0"
            y1="0"
            x2="0"
            y2="100%"
          >
            <stop stopColor={chartColor} />
          </linearGradient>
          <linearGradient
            id={`colorSplit${assetCode || ''}`}
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={chartColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={chartColor} stopOpacity="0" />
          </linearGradient>
        </defs>
      );
    }

    return (
      <defs>
        <linearGradient
          id={`stroke${assetCode || ''}`}
          x1="0"
          y1="0"
          x2="0"
          y2="100%"
        >
          <stop offset={off} stopColor={colors.text.success} />
          <stop offset={off} stopColor={colors.text.critical} />
        </linearGradient>

        <linearGradient
          id={`colorSplit${assetCode || ''}`}
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop offset="0%" stopColor={colors.text.success} stopOpacity="0.3" />
          <stop offset={off} stopColor={colors.text.success} stopOpacity="0" />
          <stop offset={off} stopColor={colors.text.critical} stopOpacity="0" />
          <stop
            offset="100%"
            stopColor={colors.text.critical}
            stopOpacity="0.3"
          />
        </linearGradient>
      </defs>
    );
  }, [chartColor, assetCode, off, colors.text.success, colors.text.critical]);

  const isChartLinear = chartMin === chartMax;

  const strokeColor = isChartLinear
    ? chartColor || colors.text.success
    : `url(#stroke${assetCode || ''})`;

  const areaColor = isChartLinear
    ? chartColor || colors.text.success
    : `url(#colorSplit${assetCode || ''})`;

  const textProps = {
    fontSize: fontSize || 12,
    fontFamily: 'Satoshi-medium',
    height: fontHeight || 18,
    textAnchor: 'end',
  };

  const areaChartMargin = {
    top: 10,
    left: width < screenSizes.tablet ? 0 : 40,
    right: width < screenSizes.tablet ? 0 : 30,
    bottom: 0,
  };

  const chartInterval = width < screenSizes.tablet ? 5 : 8;

  const margin = customMargin || areaChartMargin;

  const tickFormatter = useCallback(
    (value: string | number) => {
      return formatLargeAmount(value, currencySymbol, true);
    },
    [currencySymbol]
  );

  const xAxisTickFormatter = useCallback(
    (val: string) => {
      if (!dateFormat) return val;
      return typeof dateFormat === 'string'
        ? formatDate(dateFormat, val)
        : dateFormat(val);
    },
    [dateFormat]
  );

  const handleMouseMove = useCallback<CategoricalChartFunc>(
    (chart) => {
      if (shouldHideTooltip || !chart.activeTooltipIndex || !data || chartColor)
        return;

      const hoverIndex = chart.activeTooltipIndex;
      const hoverAmount = Number(data[hoverIndex].amount || 0);

      const newColor =
        hoverAmount >= (defaultAmount || 0)
          ? colors.text.success
          : colors.text.critical;

      if (newColor !== areaDotColor) {
        setAreaDotColor(newColor);
      }
    },
    [
      shouldHideTooltip,
      data,
      chartColor,
      defaultAmount,
      colors.text.success,
      colors.text.critical,
      areaDotColor,
    ]
  );

  const handleChartLoad = useCallback(() => {
    requestAnimationFrame(() => {
      const surface = document.getElementsByClassName(
        'recharts-surface'
      )[0] as SVGSVGElement;
      const grid = document.getElementsByClassName(
        'recharts-cartesian-grid'
      )[0] as SVGGElement;

      if (surface && grid) {
        setChartRects({
          surfaceRect: surface.getBoundingClientRect(),
          gridRect: grid.getBoundingClientRect(),
        });
      }
    });
  }, []);

  useEffect(() => {
    const timeout = setTimeout(handleChartLoad, 100);

    return () => clearTimeout(timeout);
  }, [data, handleChartLoad]);

  const areaDotColorToApply = chartColor || areaDotColor;

  const tooltipAmountColorToApply = !chartColor ? areaDotColor : undefined;

  const activeDot = useCallback(
    ({ cx, cy }: ActiveDotProps) => (
      <circle
        cx={cx}
        cy={cy}
        r={3.15}
        stroke={hexColorWithOpacity(areaDotColorToApply, 30)}
        strokeWidth={6.3}
        fill={areaDotColorToApply}
        style={{ zIndex: 1 }}
      />
    ),
    [areaDotColorToApply]
  );

  const chartTooltipContent = (
    tooltipProps: TooltipProps<ValueType, NameType>
  ) => (
    <ChartTooltip
      key={tooltipProps?.payload?.[0]?.payload?.createdAt}
      currencySymbol={currencySymbol}
      tooltipPrecision={tooltipPrecision}
      shouldHaveFadeEffect={shouldHaveFadeEffect}
      chartRects={chartRects}
      title={tooltipTitle}
      icon={tooltipIcon}
      amountColor={tooltipAmountColorToApply}
      yAxisOrientation={yAxisOrientation}
      chartMargin={margin}
      customDateFormat={tooltipTimeFormat}
      showAmountPrefix={showAmountPrefix}
      {...tooltipProps}
    />
  );

  const areaBaseValue = isBaseValueMin ? chartMin : defaultAmount;

  if (!data || !data.length) return null;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={margin} onMouseMove={handleMouseMove}>
        {shouldShowGraph && (
          <CartesianGrid
            strokeWidth={1}
            stroke={colors.borders.default + '40'}
          />
        )}

        {gradientDefinitions}

        {shouldShowGraph && !chartColor && (
          <ReferenceLine
            y={defaultAmount}
            stroke={colors.borders.default}
            strokeDasharray="3 3"
          />
        )}

        <Area
          type="monotone"
          dataKey="amount"
          stroke={strokeColor}
          fill={areaColor}
          activeDot={activeDot}
          baseValue={areaBaseValue}
        />

        <XAxis
          dataKey="createdAt"
          hide={shouldHideXAxis}
          tick={textProps}
          axisLine={false}
          tickLine={false}
          interval={getChartInterval(data.length, chartInterval)}
          tickFormatter={xAxisTickFormatter}
          includeHidden={false}
          domain={['auto', 'auto']}
        />

        <YAxis
          orientation={yAxisOrientation}
          hide={shouldHideYAxis}
          domain={domain}
          tickFormatter={tickFormatter}
          tick={textProps}
          tickLine={false}
          axisLine={false}
          allowDataOverflow
          tickCount={yAxisTickCount}
        />

        {!shouldHideTooltip && (
          <Tooltip
            wrapperStyle={{ width: '100%', height: '100%' }}
            cursor={{
              stroke: areaDotColorToApply,
              strokeWidth: 1.5,
              strokeDasharray: '2 2',
            }}
            content={chartTooltipContent}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default memo(InteractiveChart);
